import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import moment from 'moment';
import { Subject } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';
import { fadeIn, fadeOut, slideDown, slideUp } from 'src/app/animations';
import { SiteNote } from 'src/app/models/site-notes/site-note.model';
import { AuthService } from 'src/app/services/auth.service';
import { TokenHelper } from 'src/app/services/helpers/token.helper';
import { SiteNotesService } from 'src/app/services/sections/site-notes/site-notes.service';
import { GlobalSettingsService } from 'src/app/services/ui/global-settings.service';
import { ToastService } from 'src/app/services/ui/toast.service';
import { ButtonIcons } from '../../action-button/models/button-icons.enum';
import { ToastType } from '../../toast/models/toast-type.enum';
import { Document } from '../../../models/document.model';
import { DocumentsService } from 'src/app/services/sections/documents/documents.service';
import { MatSidenav } from '@angular/material/sidenav';
import { DocViewerDocumentDetails } from '../../document-viewer/document-viewer.component';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { GenericModalComponent } from '../../generic-modal/generic-modal.component';
import { UserAccountInfo } from 'src/app/models/user-account-info.model';
import { ResolvedSiteNote } from 'src/app/resolvers/site-notes/models/resolved-site-note.model';

@Component({
	selector: 'site-note-view',
	templateUrl: './site-note-view.component.html',
	styleUrls: ['../../stylesheets/job-actions.scss', '../../stylesheets/job-main.scss', '../../stylesheets/document-viewer.scss', '../../stylesheets/job-inputs.scss', './site-note-view.component.scss'],
	animations: [slideUp(100), slideDown(100), fadeIn(200), fadeOut(200)]
})
export class SiteNoteViewComponent implements OnInit, AfterViewInit, OnDestroy {

	@ViewChild('sidenav', { static: true }) public sidenav: MatSidenav;
	@ViewChild('deleteConfirmationModal', { static: true }) public deleteConfirmationModal: GenericModalComponent;

	public self = this;

	public isLoading = false;
	public isVendor = false;	
	public isDocumentsLoading = false;
	public isOpened = false;
	public viewEmail = false;
	public viewDocument = false;

	public siteNoteFormGroup: FormGroup;
	public siteNoteBackup: any;

	public deficienciesUrl: string;
	public myWorkUrl: string;
	public siteNotesUrl: string;

	public siteNote: SiteNote = null;
	public hasSiteNoteLoadingError: boolean = false;
	public siteNoteLoadingErrorMsg: string | null = null;
	public isReadonly: boolean;

	public documents: Document[] = [];
	public selectedDocumentDetails: DocViewerDocumentDetails;

	public documentLoadUnsubscriber$ = new Subject<void>();

	public readonly ButtonIcons = ButtonIcons;
	public readonly moment = moment;
	public readonly location = location;

	public userAccountInfoModel: UserAccountInfo;

	public haveAccess: boolean = false;	

	constructor(
		private route: ActivatedRoute,
		private authService: AuthService,
		private router: Router,
		private siteNotesService: SiteNotesService,
		private documentsService: DocumentsService,
		private _sanitizer: DomSanitizer,
	) {
		const resolvedSiteNote = this.route.snapshot.data.siteNote as ResolvedSiteNote;
		if(!!resolvedSiteNote) {
			this.siteNote = this.route.snapshot.data.siteNote.data;
			this.hasSiteNoteLoadingError = this.route.snapshot.data.siteNote.hasError;
			this.siteNoteLoadingErrorMsg = this.route.snapshot.data.siteNote.errorMsg;
		}
		this.isReadonly = !this.route.snapshot.paramMap?.get('siteNoteId');
	}

	ngOnInit(): void {
		if(!this.isReadonly){
			GlobalSettingsService.navigationBarActions.set(
				'site-note-close',
				{
					icon: 'close',
					listOrder: 3,
					isCustomSvgIcon: false,
					action: () => {
						this.goToListPage();
					}
				}
			);
		}

		this.populateDocumentsList();
		if (!this.isReadonly) {
			this.checkIfHaveAccess();
		}		
	}

	private checkIfHaveAccess(){
		if (this.siteNote?.siteNoteId) {
			this.siteNotesService.isSiteNoteAccessibleByUser(TokenHelper.getToken(this.route), this.siteNote?.siteNoteId).subscribe({
				next: (data: boolean) => {
					this.haveAccess = data;
					GlobalSettingsService.isSecondaryLoaderLoading = false;
				},
				error: (error: any) => {
					GlobalSettingsService.isSecondaryLoaderLoading = false;
				}
			});
		}
	}

	private goToListPage() {
		this.router.navigate(['../'], { relativeTo: this.route });
	}

	ngAfterViewInit(): void {
		setTimeout(() => {
			GlobalSettingsService.isGlobalLoaderLoading = false;	
			if(this.isReadonly || this.hasSiteNoteLoadingError) {
				GlobalSettingsService.isSecondaryLoaderLoading = false;
			}
		}, 0);
	}

	public ngOnDestroy(): void {
		this.documentLoadUnsubscriber$.next();
		this.documentLoadUnsubscriber$.complete();
		GlobalSettingsService.navigationBarActions.delete('site-note-close');
	}

	public toggleSlide(): void {
		this.isOpened = !this.isOpened;
		this.sidenav.toggle();
	}

	public populateDocumentsList(): void {
		if (!this.siteNote?.photosUrlHash || !this.siteNote.photoCount) {
			this.documents = [];
		} else {
			this.isDocumentsLoading = true;

			this.documentsService.getDocumentSummary(this.siteNote.photosUrlHash)
				.subscribe(result => {
					this.documents = result || [];
					this.isDocumentsLoading = false;
				});
		}
	}

	onDeletePressed(): void {
		this.deleteConfirmationModal.open();
	}

	public deleteSiteNote(): void {

		this.deleteConfirmationModal.hide();
		this.siteNotesService.deleteSiteNote(TokenHelper.getToken(this.route), this.siteNote.siteNoteId)
			.subscribe({
				next: (data: any) => { this.goToListPage(); }
				, error: (error) => {
					ToastService.pushToast({
						text: 'Cannot delete Site Note. Please try again!',
						icon: 'error',
						type: ToastType.Error
					});
				 }
			});
	}

	onCancelDeletion() {
		this.deleteConfirmationModal.hide();
	}

	public startCompleteMode(): void {
		this.router.navigate(['complete'], { relativeTo: this.route });
	}

	public startEditMode(): void {
		this.router.navigate(['edit'], { relativeTo: this.route });
	}

	public showDocument(document: Document): void {
		this.selectedDocumentDetails = null;
		this.viewEmail = false;
		this.viewDocument = true;

		if (!this.isOpened) {
			this.toggleSlide();
		}

		this.documentsService
			.getDocument(this.siteNote.photosUrlHash, document.documentId)
			.pipe(
				takeUntil(this.documentLoadUnsubscriber$)
			)
			.subscribe((content) => {
				this.selectedDocumentDetails = {
					documentId: document.documentId,
					content,
					documentDisplayName: document.name,
					documentDownloadName: document.name
				};
			});
	}

	public triggerCopyToast(): void {
		ToastService.pushToast({
			text: 'Link copied to clipboard!',
			icon: 'done',
			type: ToastType.Success
		});
	}

	public imageThumnail(document: Document): SafeResourceUrl | string {
		return !!document.thumbnailBase64 ? this._sanitizer.bypassSecurityTrustResourceUrl(`data:${document.contentType};base64, ${document.thumbnailBase64}`) : "../../../../assets/icons/camera-icon-black.svg";
	}



}
